import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { openCalendly } from "@/lib/constants";

const LINKS = [
  { id: NAV.linkPrograms, href: "#programs", label: "Programs" },
  { id: NAV.linkHowItWorks, href: "#how-it-works", label: "How it works" },
  { id: NAV.linkResults, href: "#results", label: "Results" },
  { id: NAV.linkContact, href: "#contact", label: "Contact" },
  { id: "nav-link-join", to: "/join-team", label: "Join Our Team" },
];

const LOGO_NAVY = "/logo/smartedge-logo-navy.png";
const LOGO_WHITE = "/logo/smartedge-logo-white.png";

const LogoImage = ({ light }) => (
  <img
    src={light ? LOGO_WHITE : LOGO_NAVY}
    alt="SmartEdge Business Solutions"
    style={{
      height: 34,
      width: "auto",
      display: "block",
      transition: "opacity 0.25s ease",
    }}
  />
);

export default function Navigation({ transparentUntilScroll = false }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!transparentUntilScroll) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentUntilScroll]);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const floating = transparentUntilScroll && !scrolled;

  return (
    <header
      className={`se-nav ${floating ? "se-nav--floating" : ""}`}
      data-testid={NAV.root}
    >
      <div
        className="container-wide flex items-center justify-between"
        style={{ height: 70 }}
      >
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center"
          aria-label="SmartEdge home"
        >
          <LogoImage light={floating} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={l.id}
                className="nav-link"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={l.id}
                className="nav-link"
                onClick={(e) => handleAnchor(e, l.href)}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid={NAV.cta}
            className={`btn-primary btn-primary--sm ${
              floating ? "btn-primary--invert" : ""
            }`}
            onClick={openCalendly}
          >
            Schedule a call
          </button>
          <button
            type="button"
            data-testid={NAV.mobileToggle}
            className="lg:hidden p-2 rounded"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            style={{ color: floating ? "#ffffff" : "var(--color-heading)" }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t"
          style={{
            borderColor: floating
              ? "rgba(255,255,255,0.08)"
              : "var(--color-border)",
            background: floating ? "rgba(6,27,49,0.9)" : "#ffffff",
          }}
        >
          <div className="container-wide flex flex-col py-3 gap-1">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`${l.id}-mobile`}
                  className="nav-link"
                  onClick={() => setOpen(false)}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  className="nav-link"
                  onClick={(e) => handleAnchor(e, l.href)}
                >
                  {l.label}
                </a>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
